import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import {
  BookOpenIcon,
  StarIcon,
  UserGroupIcon,
  SparklesIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowTrendingUpIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  ClockIcon,
  NewspaperIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    icon: BookOpenIcon,
    title: 'Unlimited Reviews',
    description:
      'No per-review fees. Earn credits and get as many reviews as you want.',
  },
  {
    icon: StarIcon,
    title: '100% Free to Start',
    description:
      'Get welcome credits to start your first campaign immediately. Plus bonus credits for every book you upload!',
  },
  {
    icon: UserGroupIcon,
    title: 'Real Reader Community',
    description:
      'Connect with passionate readers who love discovering indie books.',
  },
  {
    icon: SparklesIcon,
    title: 'Boost & Get Boosted',
    description:
      'Support authors you love. Get support from readers who love your work.',
  },
];

const steps = [
  {
    step: 1,
    title: 'Create Account',
    description: 'Sign up for free in under 30 seconds.',
  },
  {
    step: 2,
    title: 'Add Your Book',
    description: 'Upload cover, description, and Amazon link.',
  },
  {
    step: 3,
    title: 'Get Approved',
    description: 'Your book gets reviewed and approved within 24 hours.',
  },
  {
    step: 4,
    title: 'Earn & Launch',
    description: 'You get starter credits immediately! Review books to earn more, then launch your campaign.',
  },
];

const readerSteps = [
  {
    step: 1,
    title: 'Sign Up Free',
    description: 'Create your reader account in seconds.',
  },
  {
    step: 2,
    title: 'Discover Books',
    description: 'Browse the Library by genre and find your next read.',
  },
  {
    step: 3,
    title: 'Read & Review',
    description: 'Download, read via Kindle, or purchase – then write your honest review.',
  },
  {
    step: 4,
    title: 'Earn & Boost',
    description: 'Earn credits and diamonds. Use them to boost authors you love.',
  },
];

const benefits = [
  'More Amazon Reviews',
  'Better Search Ranking',
  'Increased Visibility',
  'Higher Book Sales',
  'Authentic Feedback',
  'Growing Fanbase',
];

// Helper to format numbers nicely
function formatNumber(num: number): string {
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return num.toString();
}

// Blog Section Component
function BlogSection() {
  const { data: blogData } = useQuery({
    queryKey: ['home-blog-posts'],
    queryFn: async () => {
      const res = await api.get('/blog/recent?limit=3');
      return res.data;
    },
  });

  const posts = blogData?.posts || [];

  if (posts.length === 0) return null;

  return (
    <section className="py-20 px-4 bg-dark-900/50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary-500/10 border border-primary-500/20 rounded-full px-4 py-2 mb-4">
            <NewspaperIcon className="w-4 h-4 text-primary-400" />
            <span className="text-primary-400 text-sm font-medium">From Our Blog</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-4">
            Tips & Insights for Authors
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Learn strategies to get more reviews, boost your book's visibility, and grow your author career.
          </p>
        </div>

        {/* Blog Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {posts.map((post: any) => (
            <Link
              key={post._id}
              to={`/blog/${post.slug}`}
              className="group relative bg-dark-800/50 backdrop-blur-sm border border-dark-700 rounded-2xl overflow-hidden hover:border-primary-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/10"
            >
              {/* Cover */}
              <div className="h-40 bg-gradient-to-br from-primary-600/20 to-gold-500/20 relative overflow-hidden">
                {post.coverImage ? (
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <NewspaperIcon className="w-12 h-12 text-primary-500/30" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent" />
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2 group-hover:text-primary-400 transition-colors">
                  {post.title}
                </h3>
                <p className="text-gray-400 text-sm line-clamp-2 mb-3">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <ClockIcon className="w-3.5 h-3.5" />
                    {post.readingTime} min read
                  </span>
                  <span className="text-primary-400 group-hover:text-primary-300 flex items-center gap-1">
                    Read more
                    <ArrowRightIcon className="w-3 h-3" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-primary-400 hover:text-primary-300 font-medium transition-colors"
          >
            View All Articles
            <ArrowRightIcon className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentBookIndex, setCurrentBookIndex] = useState(0);
  
  // Fetch real stats from API
  const { data: stats } = useQuery({
    queryKey: ['public-stats'],
    queryFn: async () => {
      const res = await fetch('/api/public/stats');
      const data = await res.json();
      return data.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Fetch featured/advertised books
  const { data: featuredBooks = [] } = useQuery({
    queryKey: ['featured-books'],
    queryFn: async () => {
      const res = await fetch('/api/books/ads/featured');
      const data = await res.json();
      return data.data || [];
    },
    staleTime: 1000 * 60 * 5,
  });

  // Auto-rotate featured books every 10 seconds
  useEffect(() => {
    if (featuredBooks.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentBookIndex((prev) => (prev + 1) % featuredBooks.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [featuredBooks.length]);

  return (
    <>
      <Helmet>
        <title>GetBookReviews - Free Amazon Reviews for KDP Authors | Get Book Reviews</title>
        <meta
          name="description"
          content="How to get Amazon KDP book reviews for free. GetBookReviews.org is a free ARC platform where self-published authors get authentic reviews. No fees, 100% Amazon TOS compliant. Join 500+ indie authors getting real Kindle reviews today."
        />
        <meta
          name="keywords"
          content="how to get Amazon reviews, KDP book reviews, free book reviews, Amazon KDP reviews, self-publishing reviews, indie author reviews, ARC readers, ARC platform, advance reader copy, book review service, Kindle reviews, free ARC service, Amazon TOS compliant reviews, honest book reviews, BookSprout alternative, NetGalley alternative, Pubby alternative, book marketing, book promotion, get reviews for my book, how to get book reviews on Amazon, KDP marketing, self-published book reviews, indie book reviews"
        />
        <link rel="canonical" href="https://getbookreviews.org" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="GetBookReviews - Free Book Reviews for Authors" />
        <meta
          property="og:description"
          content="The free platform where indie authors and readers help each other. Get authentic reviews, boost your Amazon ranking, and grow your readership."
        />
        <meta property="og:url" content="https://getbookreviews.org" />
        <meta property="og:image" content="https://getbookreviews.org/og-image.jpg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="GetBookReviews - Free Book Reviews" />
        <meta
          name="twitter:description"
          content="Get authentic Amazon reviews for your book. 100% free for indie authors."
        />
        <meta name="twitter:image" content="https://getbookreviews.org/og-image.jpg" />
        
        {/* Schema.org - WebApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebApplication',
            name: 'GetBookReviews',
            alternateName: 'Get Book Reviews',
            description: 'Free ARC platform for self-published authors to get authentic Amazon book reviews. The best free alternative to BookSprout and NetGalley. 100% Amazon TOS compliant.',
            url: 'https://getbookreviews.org',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD',
              description: 'Free to use - earn credits by reviewing books',
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: '4.8',
              reviewCount: '127',
            },
            featureList: [
              'Free ARC distribution',
              'Amazon TOS compliant reviews',
              'Credit-based system',
              'No subscription fees',
              'Honest review guarantee',
              'Real reader community',
            ],
          })}
        </script>
        
        {/* Schema.org - HowTo for AI Search */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: 'How to Get Amazon Reviews for Your KDP Book',
            description: 'A step-by-step guide to getting authentic Amazon reviews for your self-published Kindle book using the free ARC platform GetBookReviews.org',
            totalTime: 'P14D',
            estimatedCost: {
              '@type': 'MonetaryAmount',
              currency: 'USD',
              value: '0',
            },
            step: [
              {
                '@type': 'HowToStep',
                position: 1,
                name: 'Create a Free Account',
                text: 'Sign up for free at GetBookReviews.org. No credit card required, no subscription fees.',
                url: 'https://getbookreviews.org/register',
              },
              {
                '@type': 'HowToStep',
                position: 2,
                name: 'Add Your Book',
                text: 'Upload your book cover, add your Amazon link, description, and upload your EPUB/PDF file.',
                url: 'https://getbookreviews.org/add-book',
              },
              {
                '@type': 'HowToStep',
                position: 3,
                name: 'Get Approved',
                text: 'Your book will be reviewed and approved within 24 hours by our moderation team.',
              },
              {
                '@type': 'HowToStep',
                position: 4,
                name: 'Earn Credits',
                text: 'Review other authors books to earn credits. This builds community and earns you campaign credits.',
              },
              {
                '@type': 'HowToStep',
                position: 5,
                name: 'Launch Your Campaign',
                text: 'Use your credits to launch a review campaign. Choose from Standard (upload PDF/EPUB), Kindle (KU readers), Ebook, or Print campaigns.',
              },
              {
                '@type': 'HowToStep',
                position: 6,
                name: 'Get Reviews',
                text: 'Readers from the community will pick up your book, read it, and post honest reviews on Amazon within 2-4 weeks.',
              },
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'How do I get Amazon reviews for my KDP book?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'The best way to get Amazon reviews for your KDP book is through ARC (Advance Reader Copy) platforms like GetBookReviews.org. Sign up for free, upload your book, and connect with readers who will read your book and leave honest reviews on Amazon. This is 100% compliant with Amazon TOS.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is GetBookReviews compliant with Amazon TOS?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes. GetBookReviews follows the same model as Amazon Vine. We connect authors with readers who provide honest reviews in exchange for free ARC copies. This is explicitly allowed by Amazon as long as reviews are honest and unbiased. We never pay for positive reviews or require specific star ratings.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is GetBookReviews free?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes, GetBookReviews is 100% free. Unlike BookSprout or NetGalley, there are no subscription fees. Authors earn credits by reviewing other books, then use those credits to launch review campaigns for their own books. Readers also earn rewards for writing honest reviews.',
                },
              },
              {
                '@type': 'Question',
                name: 'What is the best free ARC service for self-published authors?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'GetBookReviews.org is one of the best free ARC services for self-published authors. Unlike paid alternatives like BookSprout ($10-30/month) or NetGalley ($450+/title), GetBookReviews is completely free. You earn credits by participating in the community and reviewing other books.',
                },
              },
              {
                '@type': 'Question',
                name: 'How long does it take to get book reviews on GetBookReviews?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'After your book is approved (within 24 hours), you can launch a review campaign immediately. Readers typically pick up books within 1-7 days, and reviews are submitted within 2-4 weeks depending on book length. The platform has an active community of readers looking for new books.',
                },
              },
              {
                '@type': 'Question',
                name: 'Can I get negative reviews on GetBookReviews?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes, and that is by design. GetBookReviews requires honest reviews - 1 to 5 stars are all equally valid. Reviewers earn the same credits regardless of the rating they give. This ensures authentic feedback and keeps the platform compliant with Amazon TOS.',
                },
              },
              {
                '@type': 'Question',
                name: 'Is GetBookReviews better than BookSprout?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'GetBookReviews offers a free alternative to BookSprout. While BookSprout charges $10-30/month, GetBookReviews is completely free. Both platforms connect authors with ARC readers, but GetBookReviews uses a credit-based system where authors earn credits by reviewing other books.',
                },
              },
              {
                '@type': 'Question',
                name: 'How do I get more reviews for my self-published book on Amazon?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'To get more reviews for your self-published book: 1) Use ARC platforms like GetBookReviews.org to connect with readers, 2) Add a review request at the end of your book, 3) Build an email list and ask subscribers to review, 4) Join author communities and do review swaps. Never pay for reviews or offer incentives for positive reviews as this violates Amazon TOS.',
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-dark-950 bg-book-pattern">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-900/60 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
                <img src="/favicon.png" alt="GetBookReviews" className="w-8 h-8 sm:w-9 sm:h-9 transition-transform group-hover:scale-110" />
                <span className="text-lg sm:text-xl font-serif font-bold gradient-text">
                  GetBookReviews
                </span>
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden sm:flex items-center gap-2">
                <Link
                  to="/library"
                  className="px-4 py-2 rounded-xl text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-200"
                >
                  Library
                </Link>
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-xl text-gray-300 hover:text-white hover:bg-white/10 transition-all duration-200"
                >
                  Sign In
                </Link>
                <Link 
                  to="/register" 
                  className="ml-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-400 hover:to-primary-500 text-white font-medium shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 transition-all duration-200 hover:scale-105"
                >
                  Get Started
                </Link>
              </div>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="sm:hidden p-2.5 rounded-xl text-gray-300 hover:text-white bg-white/5 hover:bg-white/15 border border-white/10 transition-all duration-200"
              >
                {mobileMenuOpen ? (
                  <XMarkIcon className="w-5 h-5" />
                ) : (
                  <Bars3Icon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          
          {/* Mobile Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="sm:hidden bg-dark-900/80 backdrop-blur-xl border-t border-white/10">
              <div className="px-4 py-4 space-y-2">
                <Link
                  to="/library"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-center py-3 px-4 rounded-xl text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/15 transition-all duration-200"
                >
                  Library
                </Link>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-center py-3 px-4 rounded-xl text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/15 transition-all duration-200"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full text-center py-3 px-4 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-400 hover:to-primary-500 text-white font-medium shadow-lg shadow-primary-500/25 transition-all duration-200"
                >
                  Get Started
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Hero Section with Featured Book */}
        <section className="pt-24 sm:pt-32 pb-16 sm:pb-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
              {/* Left: Title & CTA */}
              <div className="flex-1 text-center lg:text-left">
                <div className="flex flex-wrap items-center gap-3 justify-center lg:justify-start mb-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/20 border border-primary-500/30">
                    <SparklesIcon className="w-4 h-4 text-primary-400" />
                    <span className="text-primary-400 font-medium text-sm">100% Free Platform</span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30">
                    <ShieldCheckIcon className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 font-medium text-sm">Amazon TOS Compliant</span>
                  </div>
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 sm:mb-6">
                  Get Real Reviews for Your{' '}
                  <span className="gradient-text">Self-Published Book</span>
                </h1>
                <p className="text-base sm:text-xl text-gray-400 max-w-xl mx-auto lg:mx-0 mb-6">
                  The free community where indie authors and readers help each other. 
                  Earn credits by reviewing books – use them to launch your own campaigns.
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-8">
                  {['No subscription', 'No per-review fees', 'No hidden costs'].map((item) => (
                    <span key={item} className="inline-flex items-center gap-1.5 text-sm text-gray-300">
                      <CheckCircleIcon className="w-4 h-4 text-green-400" />
                      {item}
                    </span>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                  <Link
                    to="/register"
                    className="btn-primary inline-flex items-center justify-center gap-2 text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4"
                  >
                    Start Getting Reviews
                    <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  </Link>
                  <Link
                    to="/library"
                    className="btn-secondary inline-flex items-center justify-center gap-2 text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4"
                  >
                    Explore Library
                  </Link>
                </div>
              </div>

              {/* Right: Featured Book Carousel (smaller) */}
              {featuredBooks.length > 0 && (
                <div className="flex-shrink-0 w-full lg:w-auto">
                  <div className="relative w-[280px] sm:w-[320px] mx-auto lg:mx-0 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500/20 border border-gold-500/30 mb-6">
                      <SparklesIcon className="w-4 h-4 text-gold-400" />
                      <span className="text-gold-400 font-medium text-sm">Sponsored</span>
                      <span className="text-[10px] text-gray-500" title="Paid placement. Sponsored books get visibility only; this never affects reviews or ratings.">Ad</span>
                    </div>
                    
                    {/* All books stacked, only current visible */}
                    <div className="relative h-[380px] sm:h-[420px]">
                      {featuredBooks.map((book: any, idx: number) => (
                        <div 
                          key={book._id || idx}
                          className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
                            idx === currentBookIndex 
                              ? 'opacity-100 z-10' 
                              : 'opacity-0 z-0 pointer-events-none'
                          }`}
                        >
                          {/* Book Cover */}
                          <a 
                            href={book.amazonURL || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="relative group mb-5 h-[240px] sm:h-[280px] w-[160px] sm:w-[190px] mx-auto block cursor-pointer"
                          >
                            <div className="absolute -inset-3 bg-gradient-to-r from-primary-500 to-gold-500 rounded-xl blur-xl opacity-40 group-hover:opacity-60 transition-opacity"></div>
                            <img
                              src={book.coverUrl || '/placeholder-cover.jpg'}
                              alt={book.title}
                              className="absolute inset-0 w-full h-full object-contain rounded-lg shadow-2xl group-hover:scale-105 transition-transform duration-300"
                            />
                          </a>
                          
                          {/* Book Info */}
                          <div className="text-center px-2">
                            <h3 className="text-xl font-serif font-bold text-white mb-1 line-clamp-1">
                              {book.title}
                            </h3>
                            <p className="text-sm text-gray-400 mb-4">
                              by {book.author}
                            </p>
                            
                            <div className="flex gap-3 justify-center">
                              {book.amazonURL && (
                                <a
                                  href={book.amazonURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#FF9900] hover:bg-[#FF9900]/90 text-dark-900 font-semibold text-sm transition-colors"
                                >
                                  Amazon
                                </a>
                              )}
                              <Link
                                to="/login"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm transition-colors"
                              >
                                Read
                                <ChevronRightIcon className="w-4 h-4" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Progress dots */}
                    {featuredBooks.length > 1 && (
                      <div className="flex justify-center gap-2 mt-2">
                        {featuredBooks.map((_: any, idx: number) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentBookIndex(idx)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              idx === currentBookIndex
                                ? 'w-6 bg-primary-500'
                                : 'bg-dark-600 hover:bg-dark-500'
                            }`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 border-y border-dark-700 bg-dark-900/50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              <div className="relative">
                {stats?.usersThisWeek > 0 && (
                  <div className="absolute -top-2 right-0 flex items-center gap-1 text-xs text-green-400">
                    <ArrowTrendingUpIcon className="w-3 h-3" />
                    +{stats.usersThisWeek} this week
                  </div>
                )}
                <div className="text-4xl font-bold gradient-text mb-2">
                  {stats?.users ? formatNumber(stats.users) : '–'}
                </div>
                <div className="text-gray-400">Registered Authors</div>
              </div>
              <div className="relative">
                {stats?.booksThisWeek > 0 && (
                  <div className="absolute -top-2 right-0 flex items-center gap-1 text-xs text-green-400">
                    <ArrowTrendingUpIcon className="w-3 h-3" />
                    +{stats.booksThisWeek} this week
                  </div>
                )}
                <div className="text-4xl font-bold gradient-text mb-2">
                  {stats?.books ? formatNumber(stats.books) : '–'}
                </div>
                <div className="text-gray-400">Published Books</div>
              </div>
              <div className="relative">
                {stats?.reviewsThisWeek > 0 && (
                  <div className="absolute -top-2 right-0 flex items-center gap-1 text-xs text-green-400">
                    <ArrowTrendingUpIcon className="w-3 h-3" />
                    +{stats.reviewsThisWeek} this week
                  </div>
                )}
                <div className="text-4xl font-bold gradient-text mb-2">
                  {stats?.reviews ? formatNumber(stats.reviews) : '–'}
                </div>
                <div className="text-gray-400">Reviews Written</div>
              </div>
              <div>
                <div className="text-4xl font-bold gradient-text mb-2">
                  {stats?.avgRating ? `${stats.avgRating}★` : '–'}
                </div>
                <div className="text-gray-400">Average Rating</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-4">
                Why Authors Choose GetBookReviews
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Unlike other platforms, we're 100% free. No subscriptions, no per-review fees. 
                Just a community helping each other succeed.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="card-dark text-center relative hover:border-primary-500/50 transition-colors">
                  <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-primary-500/20 to-primary-700/20 border border-primary-500/30 flex items-center justify-center mb-4">
                    <feature.icon className="w-7 h-7 text-primary-400" />
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-20 px-4 bg-dark-900/50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-4">
                How It Works
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Whether you're a self-published author or a passionate reader – 
                start earning and helping in just 4 simple steps.
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Authors Column */}
              <div className="card-dark border-primary-500/30">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/20 border border-primary-500/30 mb-4">
                    <span className="text-2xl">✍️</span>
                    <span className="text-primary-400 font-bold">For Authors</span>
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white mb-2">Launch Your Book Campaign</h3>
                  <p className="text-gray-400 text-sm">Get discovered by readers who love your genre</p>
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/20 border border-green-500/30">
                    <span className="text-green-400 text-sm font-medium">🎁 You get starter credits to launch immediately!</span>
                  </div>
                </div>
                <div className="space-y-6">
                  {steps.map((item) => (
                    <div key={item.step} className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-xl font-bold text-white flex-shrink-0 shadow-glow">
                        {item.step}
                      </div>
                      <div>
                        <h3 className="text-lg font-serif font-bold text-white mb-1">
                          {item.title}
                        </h3>
                        <p className="text-gray-400 text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 text-center">
                  <Link to="/register" className="btn-primary inline-flex items-center gap-2">
                    I'm an Author
                    <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              
              {/* Readers Column */}
              <div className="card-dark border-gold-500/30">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500/20 border border-gold-500/30 mb-4">
                    <span className="text-2xl">📖</span>
                    <span className="text-gold-400 font-bold">For Readers</span>
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white mb-2">Discover & Support Authors</h3>
                  <p className="text-gray-400 text-sm">Read great books and earn rewards</p>
                </div>
                <div className="space-y-6">
                  {readerSteps.map((item) => (
                    <div key={item.step} className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gold-500 to-gold-700 flex items-center justify-center text-xl font-bold text-dark-900 flex-shrink-0">
                        {item.step}
                      </div>
                      <div>
                        <h3 className="text-lg font-serif font-bold text-white mb-1">
                          {item.title}
                        </h3>
                        <p className="text-gray-400 text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 text-center">
                  <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gold-500 hover:bg-gold-400 text-dark-900 font-semibold transition-colors">
                    I'm a Reader
                    <ArrowRightIcon className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Amazon TOS Compliant */}
        <section className="py-20 px-4 bg-gradient-to-br from-green-900/20 to-dark-900">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30 mb-6">
                <ShieldCheckIcon className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-semibold">100% Amazon TOS Compliant</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-4">
                Safe, Legal, and Ethical Reviews
              </h2>
              <p className="text-gray-400 max-w-3xl mx-auto text-lg">
                GetBookReviews follows the same model as Amazon's official Vine program. 
                We connect authors with real readers who provide honest, unbiased reviews.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              <div className="card-dark border-green-500/30 hover:border-green-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Honest Reviews Only</h3>
                </div>
                <p className="text-gray-400 text-sm">
                  We require honest reviews – 1 to 5 stars are all equally valid. No pressure for positive ratings.
                </p>
              </div>
              
              <div className="card-dark border-green-500/30 hover:border-green-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">No Payment for Reviews</h3>
                </div>
                <p className="text-gray-400 text-sm">
                  Credits reward activity and participation, not star ratings. Reviewers earn the same for any honest review.
                </p>
              </div>
              
              <div className="card-dark border-green-500/30 hover:border-green-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Free ARCs Allowed</h3>
                </div>
                <p className="text-gray-400 text-sm">
                  Amazon explicitly allows free Advance Reader Copies in exchange for honest reviews – just like their Vine program.
                </p>
              </div>
              
              <div className="card-dark border-green-500/30 hover:border-green-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">No Profile Monitoring</h3>
                </div>
                <p className="text-gray-400 text-sm">
                  We don't track Amazon profiles. However, we have robust security measures against abuse, including review moderation and a report button on every review.
                </p>
              </div>
              
              <div className="card-dark border-green-500/30 hover:border-green-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Real Readers</h3>
                </div>
                <p className="text-gray-400 text-sm">
                  Our community consists of genuine book lovers who read the full book before reviewing.
                </p>
              </div>
              
              <div className="card-dark border-green-500/30 hover:border-green-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">Transparent Disclosure</h3>
                </div>
                <p className="text-gray-400 text-sm">
                  Reviewers are encouraged to disclose they received a free copy – full transparency with Amazon.
                </p>
              </div>
              
              <div className="card-dark border-green-500/30 hover:border-green-500/50 transition-colors">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircleIcon className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white">No Review Exchange</h3>
                </div>
                <p className="text-gray-400 text-sm">
                  Our system automatically prevents review exchanges. You cannot review books from authors who have already reviewed your books – eliminating "you review me, I review you" schemes.
                </p>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-gray-500 text-sm max-w-2xl mx-auto">
                <span className="text-green-400 font-semibold">Important:</span> GetBookReviews is NOT a "review selling" service. 
                We are a community platform that connects authors with readers who genuinely want to discover new books. 
                This is exactly what Amazon allows and encourages.
              </p>
            </div>
          </div>
        </section>

        {/* Campaign Types */}
        <section className="py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-4">
                4 Campaign Types
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Choose how readers access your book. Each type works differently to match your publishing strategy.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="card-dark text-center hover:border-primary-500/50 transition-colors">
                <div className="text-4xl mb-4">📚</div>
                <h3 className="text-lg font-serif font-bold text-white mb-2">Standard</h3>
                <p className="text-gray-400 text-sm">Upload your EPUB/PDF. Readers download directly from the platform.</p>
              </div>
              <div className="card-dark text-center hover:border-primary-500/50 transition-colors">
                <div className="text-4xl mb-4">📱</div>
                <h3 className="text-lg font-serif font-bold text-white mb-2">Kindle</h3>
                <p className="text-gray-400 text-sm">Readers access via Kindle Unlimited.</p>
              </div>
              <div className="card-dark text-center hover:border-primary-500/50 transition-colors">
                <div className="text-4xl mb-4">📖</div>
                <h3 className="text-lg font-serif font-bold text-white mb-2">Ebook</h3>
                <p className="text-gray-400 text-sm">Readers purchase the ebook.</p>
              </div>
              <div className="card-dark text-center hover:border-primary-500/50 transition-colors">
                <div className="text-4xl mb-4">📕</div>
                <h3 className="text-lg font-serif font-bold text-white mb-2">Print</h3>
                <p className="text-gray-400 text-sm">Readers order the printed version.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 px-4 bg-dark-900/50">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-6">
                  Boost Your Amazon Ranking
                </h2>
                <p className="text-gray-400 mb-8">
                  Reviews are the #1 factor for success on Amazon. More reviews mean better visibility, 
                  higher rankings, and more sales. GetBookReviews helps you get there – for free.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircleIcon className="w-5 h-5 text-primary-400 flex-shrink-0" />
                      <span className="text-gray-300">{benefit}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-8">
                  <Link to="/register" className="btn-primary inline-flex items-center gap-2">
                    Get Started Free
                    <ArrowRightIcon className="w-5 h-5" />
                  </Link>
                </div>
              </div>
              <div className="relative">
                <div className="aspect-square rounded-2xl bg-gradient-to-br from-primary-500/10 to-gold-500/10 border border-dark-700 flex items-center justify-center">
                  <div className="text-center">
                    <BookOpenIcon className="w-32 h-32 text-primary-500/50 mx-auto mb-4" />
                    <p className="text-2xl font-serif text-white/50">
                      Your Book Here
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Section */}
        <BlogSection />

        {/* CTA */}
        <section className="py-20 px-4 bg-gradient-to-br from-primary-900/30 to-dark-900">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-white mb-6">
              Ready for More Reviews?
            </h2>
            <p className="text-xl text-gray-400 mb-8">
              Join thousands of successful authors and take your book
              to the next level.
            </p>
            <Link
              to="/register"
              className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
            >
              Register for Free
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-dark-700">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <Link to="/" className="flex items-center gap-2 mb-4">
                  <img src="/favicon.png" alt="GetBookReviews" className="w-8 h-8" />
                  <span className="text-lg font-serif font-bold text-white">
                    GetBookReviews
                  </span>
                </Link>
                <p className="text-gray-400 text-sm">
                  The platform for authentic book reviews.
                </p>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Platform</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li><Link to="/library" className="hover:text-white transition">Library</Link></li>
                  <li><Link to="/register" className="hover:text-white transition">Register</Link></li>
                  <li><Link to="/login" className="hover:text-white transition">Sign In</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Legal</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li><Link to="/terms" className="hover:text-white transition">Terms of Service</Link></li>
                  <li><Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link></li>
                  <li><Link to="/imprint" className="hover:text-white transition">Imprint</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-4">Support</h4>
                <ul className="space-y-2 text-sm text-gray-400">
                  <li><Link to="/contact" className="hover:text-white transition">Contact</Link></li>
                  <li><Link to="/faq" className="hover:text-white transition">FAQ</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-dark-700 pt-8 text-center text-sm text-gray-500">
              © {new Date().getFullYear()} GetBookReviews. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
